import { z } from 'zod';
import { auditFieldsSchema, emailSchema, idSchema, paginated, paginationQuerySchema, phoneSchema } from './common.js';
import { permissionCodeSchema } from './permissions.js';
/**
 * Situação do usuário na lista, calculada pela API — uma regra só:
 * desativado > bloqueado (errou a senha demais) > convite pendente (nunca
 * entrou) > ativo.
 */
export const USER_STATUSES = ['ATIVO', 'PENDENTE', 'BLOQUEADO', 'INATIVO'];
export const userStatusSchema = z.enum(USER_STATUSES);
export const USER_STATUS_LABELS = {
    ATIVO: 'Ativo',
    PENDENTE: 'Convite pendente',
    BLOQUEADO: 'Bloqueado',
    INATIVO: 'Inativo',
};
export const userSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    unitId: idSchema.nullable(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    jobTitle: z.string().nullable(),
    departmentId: idSchema.nullable(),
    department: z.object({ id: idSchema, name: z.string() }).nullable(),
    notes: z.string().nullable(),
    isActive: z.boolean(),
    mustChangePassword: z.boolean(),
    lastLoginAt: z.string().nullable(),
    /** Até quando o login está travado por excesso de senha errada. */
    lockedUntil: z.string().nullable(),
    status: userStatusSchema,
    profiles: z.array(z.object({ id: idSchema, slug: z.string(), name: z.string() })),
});
const passwordSchema = z
    .string()
    .min(10, 'A senha precisa de pelo menos 10 caracteres')
    .max(200)
    .regex(/[a-zA-Z]/, 'A senha precisa conter letras')
    .regex(/\d/, 'A senha precisa conter números');
export const userCreateSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome').max(120),
    email: emailSchema,
    phone: phoneSchema.optional().nullable(),
    jobTitle: z.string().trim().max(80).optional().nullable(),
    departmentId: idSchema.optional().nullable(),
    notes: z.string().trim().max(500, 'Observações com no máximo 500 caracteres').optional().nullable(),
    password: passwordSchema,
    unitId: idSchema.optional().nullable(),
    profileIds: z.array(idSchema).min(1, 'Selecione ao menos um perfil'),
    isActive: z.boolean().default(true),
    mustChangePassword: z.boolean().default(true),
});
export const userUpdateSchema = userCreateSchema.omit({ password: true }).partial().extend({
    profileIds: z.array(idSchema).min(1, 'Selecione ao menos um perfil').optional(),
});
export const userResetPasswordSchema = z.object({ password: passwordSchema });
export const userListQuerySchema = paginationQuerySchema.extend({
    profileId: idSchema.optional(),
    departmentId: idSchema.optional(),
    status: userStatusSchema.optional(),
    isActive: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const userListResponseSchema = paginated(userSchema);
/** Os cartões do topo da tela de usuários. */
export const userSummarySchema = z.object({
    total: z.number().int(),
    active: z.number().int(),
    pending: z.number().int(),
    blocked: z.number().int(),
    inactive: z.number().int(),
    /** Cadastrados nos últimos 30 dias. */
    newLast30Days: z.number().int(),
});
/* ---------------------------------- Perfis --------------------------------- */
export const profileSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    fullAccess: z.boolean(),
    /** Perfis de sistema não podem ser excluídos (mas podem ser editados). */
    isSystem: z.boolean(),
    isActive: z.boolean(),
    permissions: z.array(permissionCodeSchema),
    userCount: z.number().int(),
});
export const profileInputSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome do perfil').max(80),
    description: z.string().trim().max(300).optional().nullable(),
    fullAccess: z.boolean().default(false),
    isActive: z.boolean().default(true),
    permissions: z.array(permissionCodeSchema).default([]),
});
export const profileListResponseSchema = paginated(profileSchema);
//# sourceMappingURL=user.js.map